import strings from 'strings'

export const STATUS_META = {
  Compliant: { labelKey: 'TAXES_STATUS_COMPLIANT', accent: '#2f6b52', tint: '#E7F0EA' },
  ActiveResolution: { labelKey: 'TAXES_STATUS_ACTIVE_RESOLUTION', accent: '#5b49be', tint: '#EEEAFB' },
  AtRisk: { labelKey: 'TAXES_STATUS_AT_RISK', accent: '#c2542b', tint: '#FBEEE6' },
  NotCompliant: { labelKey: 'TAXES_STATUS_NOT_COMPLIANT', accent: '#c2542b', tint: '#FBEAE3' },
  DataPending: { labelKey: 'TAXES_STATUS_DATA_PENDING', accent: '#5f6b64', tint: '#EDEFEE' },
  NotMonitored: { labelKey: 'TAXES_STATUS_NOT_MONITORED', accent: '#5f6b64', tint: '#EDEFEE' }
}

export const INK = '#152420'
export const MUTED = '#68746d'
export const FAINT = '#8d968f'
export const CRITICAL = '#c2542b'
export const PURPLE = '#5b49be'
export const PAGE = '#F3F5F3'
export const CARD_BORDER = '#E8EBE9'
export const NEUTRAL_TINT = '#EDEFEE'
export const INSET = '#F4F6F4'
export const EM_DASH = '—'

export const getStatusMeta = (status) => STATUS_META[status] || {
  labelKey: null,
  rawLabel: status || EM_DASH,
  accent: '#79857f',
  tint: 'rgba(121,133,127,0.14)'
}

export const getStatusLabel = (status) => {
  const meta = getStatusMeta(status)
  return meta.labelKey ? strings[meta.labelKey] : meta.rawLabel
}

const isNum = (value) => typeof value === 'number' && Number.isFinite(value)

export const formatCurrency = (amount) => {
  if (!isNum(amount)) return EM_DASH
  const hasCents = Math.abs(amount % 1) > 0.004
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0
  }).format(amount)
}

export const formatCount = (value) => (isNum(value) ? String(value) : EM_DASH)

// "Aug 5" - how the prose refers to a filing date.
export const formatMonthDay = (value) => {
  if (!value) return EM_DASH
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EM_DASH
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

// "May 19, 2026" - agreement start dates, where the year carries weight.
export const formatLongDate = (value) => {
  if (!value) return EM_DASH
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EM_DASH
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}


export const formatPulledAt = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const stamp = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Chicago'
  })
  return `${stamp.replace(',', ',')} CT`
}

export const formatDate = (value) => {
  if (!value) return EM_DASH
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EM_DASH
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${month}/${day}/${date.getUTCFullYear()}`
}


export const formatTin = (result) => {
  const digits = String(result?.tin || '').replace(/\D/g, '')
  if (!digits) return null
  if (result?.tinCensored || result?.isTinCensored) {
    return `•••• ${digits.slice(-4)}`
  }
  if (result?.type === 'Business' && digits.length === 9) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`
  }
  if (digits.length === 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
  }
  return digits
}

export const collectPeriods = (result) => {
  const periods = []
  const push = (list) => (list || []).forEach((period) => period && periods.push(period))

  push(result?.installmentAgreementSummary?.periodsNotCoveredByActiveIA)
  ;(result?.liens || []).forEach((lien) => push(lien.securedPeriods))
  ;(result?.installmentAgreements || []).forEach((agreement) => push(agreement.coveredPeriods))

  return periods
}

export const findPeriodForYear = (periods, year) => periods.find((period) => period.taxYear === year) || null

export const formatPeriodShort = (period) => (period ? `${period.formName} · ${period.taxPeriod}` : null)

export const formatPeriodFull = (period) => (
  period ? `${period.formName} · ${period.taxYear} · P${period.taxPeriod}` : EM_DASH
)

export const formatPeriodList = (list) => {
  if (!list || !list.length) return EM_DASH
  return list.map(formatPeriodFull).join(', ')
}

// The form the report is filed under, when anything in the payload names one.
export const primaryFormName = (periods) => (periods.length ? periods[0].formName : null)

export const isPending = (record) => record?.currentStatus?.status === 'DataPending'

const NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten']

const countWord = (count) => NUMBER_WORDS[count] || String(count)

const owingYears = (years) => years.filter((row) => row.amountDue).map((row) => row.year)

const joinList = (items) => {
  if (items.length <= 1) return items.join('')
  const last = items[items.length - 1]
  return `${items.slice(0, -1).join(', ')} ${strings.TAXES_AND} ${last}`
}


const genericHeadline = (result) => {
  const lienSummary = result.lienSummary || {}
  const iaSummary = result.installmentAgreementSummary || {}
  const amountDue = isNum(result.amountDue) ? result.amountDue : 0
  const missing = isNum(result.numMissingReturns) ? result.numMissingReturns : 0
  const activeIa = (result.installmentAgreements || []).find((agreement) => agreement.status === 'Active')
  const uncovered = isNum(iaSummary.balanceNotCoveredByActiveIA) ? iaSummary.balanceNotCoveredByActiveIA : 0

  const clauses = []
  if (amountDue) clauses.push(`${formatCurrency(amountDue)} ${strings.TAXES_OWED}`)

  if (lienSummary.hasActiveLienSecuringNonZeroBalance && lienSummary.mostRecentActiveLienFilingDate) {
    clauses.push(`${strings.TAXES_SECURED_BY_LIEN_FILED} ${formatMonthDay(lienSummary.mostRecentActiveLienFilingDate)}`)
  }

  if (activeIa && !uncovered) {
    clauses.push(`${strings.TAXES_COVERED_BY_IA_STARTED} ${formatLongDate(activeIa.startDate)}`)
  } else if (uncovered && amountDue && uncovered >= amountDue && !lienSummary.hasActiveLienSecuringNonZeroBalance) {
    clauses.push(strings.TAXES_NONE_COVERED_BY_IA)
  } else if (uncovered && amountDue && uncovered < amountDue) {
    clauses.push(`${formatCurrency(uncovered)} ${strings.TAXES_OUTSIDE_ANY_PLAN}`)
  }

  if (missing) {
    clauses.push(`${missing} ${missing === 1 ? strings.TAXES_RETURN_STILL_MISSING : strings.TAXES_RETURNS_STILL_MISSING}`)
  }

  if (!clauses.length) return strings.TAXES_ALL_CLEAR_HEADLINE
  if (clauses.length > 2) {
    const last = clauses.pop()
    return `${clauses.join(', ')}, ${strings.TAXES_AND} ${last}.`
  }
  return `${clauses.join(', ')}.`
}

export const buildHeadline = (record) => {
  const status = record?.currentStatus?.status
  const result = record?.result || {}
  const years = result.balanceByYear || []
  const amountDue = isNum(result.amountDue) ? result.amountDue : 0
  const uncovered = isNum(result.installmentAgreementSummary?.balanceNotCoveredByActiveIA)
    ? result.installmentAgreementSummary.balanceNotCoveredByActiveIA
    : 0

  if (status === 'DataPending') return strings.TAXES_PENDING_HEADLINE_NEW

  if (status === 'Compliant') {
    if (!years.length) return strings.TAXES_ALL_CLEAR_HEADLINE
    const latest = Math.max(...years.map((row) => row.year))
    return `${strings.TAXES_FILED_AND_PAID_THROUGH} ${latest}. ${strings.TAXES_NO_BALANCE_NO_LIENS}`
  }

  if (status === 'ActiveResolution' && amountDue && !uncovered) {
    return `${formatCurrency(amountDue)} ${strings.TAXES_OWED}, ${strings.TAXES_FULLY_COVERED}.`
  }

  if (status === 'AtRisk' && amountDue) {
    const owing = owingYears(years)
    const opener = owing.length
      ? `${formatCurrency(amountDue)} ${owing.length === 1 ? strings.TAXES_OWED_ON_TAX_YEAR : strings.TAXES_OWED_ON_TAX_YEARS} ${joinList(owing.map(String))}`
      : `${formatCurrency(amountDue)} ${strings.TAXES_OWED}`
    const clauses = []
    if (uncovered >= amountDue) clauses.push(strings.TAXES_WITH_NO_PAYMENT_PLAN_CLAUSE)
    if (!(result.liens || []).length) clauses.push(strings.TAXES_AND_NO_LIEN_YET)
    return clauses.length ? `${opener}, ${clauses.join(' ')}.` : `${opener}.`
  }

  return genericHeadline(result)
}


export const buildWhyThisMatters = (record) => {
  const status = record?.currentStatus?.status
  const result = record?.result || {}
  const years = result.balanceByYear || []
  const missing = isNum(result.numMissingReturns) ? result.numMissingReturns : 0
  const hasLien = !!result.lienSummary?.hasActiveLienSecuringNonZeroBalance

  if (status === 'DataPending') return strings.TAXES_WHY_DATA_PENDING
  if (status === 'NotMonitored') return strings.TAXES_WHY_NOT_MONITORED
  if (status === 'AtRisk') return strings.TAXES_WHY_AT_RISK
  if (status === 'ActiveResolution') return strings.TAXES_WHY_ACTIVE_RESOLUTION

  if (status === 'Compliant') {
    return `${countWord(years.length)} ${strings.TAXES_WHY_COMPLIANT_PREFIX}`
  }

  // NotCompliant, and anything unrecognised that still owes.
  const lead = hasLien ? strings.TAXES_WHY_NOT_COMPLIANT_LIEN : strings.TAXES_WHY_NOT_COMPLIANT_BALANCE
  if (!missing) return `${lead}. ${strings.TAXES_WHY_NOT_COMPLIANT_ACTION_NO_MISSING}`
  const missingClause = missing === 1
    ? strings.TAXES_WHY_NOT_COMPLIANT_MISSING_ONE
    : `and ${missing} ${strings.TAXES_WHY_NOT_COMPLIANT_MISSING_MANY}`
  return `${lead}, ${missingClause}. ${strings.TAXES_WHY_NOT_COMPLIANT_ACTION}`
}

/** The four figures across the top, each with the note that explains it. */
export const buildTileNotes = (record) => {
  const result = record?.result || {}
  if (isPending(record)) {
    return {
      balance: strings.TAXES_NOTE_TRANSCRIPTS_IN_FLIGHT,
      liens: strings.TAXES_NOTE_NOT_RETURNED,
      plan: strings.TAXES_NOTE_NOT_RETURNED,
      returns: strings.TAXES_NOTE_NOT_RETURNED
    }
  }

  const owing = owingYears(result.balanceByYear || [])
  const activeLiens = (result.liens || []).filter((lien) => lien.status === 'Active').length
  const uncovered = isNum(result.installmentAgreementSummary?.balanceNotCoveredByActiveIA)
    ? result.installmentAgreementSummary.balanceNotCoveredByActiveIA
    : 0
  const missing = isNum(result.numMissingReturns) ? result.numMissingReturns : 0

  return {
    balance: !result.amountDue
      ? strings.TAXES_NOTE_NOTHING_OWED
      : (owing.length === 1
        ? strings.TAXES_NOTE_ACROSS_ONE_YEAR
        : `${strings.TAXES_NOTE_ACROSS_YEARS_PREFIX} ${owing.length} ${strings.TAXES_NOTE_ACROSS_YEARS_SUFFIX}`),
    liens: activeLiens === 0
      ? strings.TAXES_NOTE_NO_FILINGS
      : (activeLiens === 1 ? strings.TAXES_NOTE_ONE_ACTIVE_FILING : `${activeLiens} ${strings.TAXES_NOTE_ACTIVE_FILINGS_SUFFIX}`),
    plan: uncovered ? strings.TAXES_NOTE_NO_AGREEMENT_IN_PLACE : strings.TAXES_NOTE_INSIDE_AN_AGREEMENT,
    returns: !missing
      ? strings.TAXES_NOTE_ALL_YEARS_FILED
      : (missing === 1 ? strings.TAXES_NOTE_RETURN_NOT_FILED : strings.TAXES_NOTE_RETURNS_NOT_FILED)
  }
}

export const countActiveLiens = (result) => (result?.liens || []).filter((lien) => lien.status === 'Active').length

export const countActiveAgreements = (result) => (result?.installmentAgreements || []).filter((ia) => ia.status === 'Active').length


export const buildYearSubtitle = (row) => {
  const parts = [`${strings.TAXES_PRINCIPAL_INLINE} ${formatCurrency(row.principal)}`]
  if (row.interest) parts.push(`${strings.TAXES_INTEREST_INLINE} ${formatCurrency(row.interest)}`)
  if (row.penalties) parts.push(`${strings.TAXES_PENALTIES_INLINE} ${formatCurrency(row.penalties)}`)

  let closer
  if (row.allCredits) {
    parts.push(`${strings.TAXES_CREDITS_INLINE} ${formatCurrency(row.allCredits)}`)
    closer = row.amountDue ? '' : strings.TAXES_IA_SETTLED_SUFFIX
  } else if (row.amountDue) {
    closer = strings.TAXES_NO_CREDITS_APPLIED_SENTENCE
  } else {
    parts.push(`${strings.TAXES_CREDITS_INLINE} ${formatCurrency(0)}`)
    closer = strings.TAXES_IA_SETTLED_SUFFIX
  }

  return closer ? `${parts.join(', ')}. ${closer}` : `${parts.join(', ')}.`
}

export const buildLienSummarySentence = (result, pending) => {
  if (pending) return strings.TAXES_NOTHING_RETURNED

  const liens = result?.liens || []
  if (!liens.length) {

    const uncovered = result?.installmentAgreementSummary?.balanceNotCoveredByActiveIA
    if (isNum(uncovered) && uncovered) return strings.TAXES_NO_LIENS_ON_FILE_DRAW
    return isNum(result?.amountDue) && result.amountDue
      ? strings.TAXES_NO_LIENS_ON_FILE
      : strings.TAXES_NO_LIENS_ON_FILE_EVER
  }

  const active = liens.filter((lien) => lien.status === 'Active')
  const lead = active[0] || liens[0]
  const lienSummary = result.lienSummary || {}
  const filingDate = formatMonthDay(lienSummary.mostRecentActiveLienFilingDate || lead.filingDate)
  const count = active.length || liens.length
  const opener = count === 1
    ? `${count} ${strings.TAXES_ACTIVE_LIEN_FILED} ${filingDate}`
    : `${count} ${strings.TAXES_ACTIVE_LIENS_MOST_RECENT} ${filingDate}`

  const secured = isNum(lienSummary.totalBalanceSecuredByLiens) && lienSummary.totalBalanceSecuredByLiens
    ? lienSummary.totalBalanceSecuredByLiens
    : lead.currentSecuredBalance

  let sentence = `${opener}, ${strings.TAXES_SECURING} ${formatCurrency(secured)}`
  const period = (lead.securedPeriods || [])[0]
  if (period && count === 1) {
    sentence += ` ${strings.TAXES_AGAINST_FORM} ${period.formName}, ${strings.TAXES_TAX_YEAR} ${period.taxYear}`
  }
  sentence += '.'

  if (count === 1) {
    sentence += lead.fullyRemovedDate
      ? ` ${strings.TAXES_REMOVED_ON} ${formatMonthDay(lead.fullyRemovedDate)}.`
      : ` ${strings.TAXES_NOT_REMOVED_SENTENCE}`
  }
  return sentence
}

export const buildAgreementSummarySentence = (result, pending) => {
  if (pending) return strings.TAXES_NOTHING_RETURNED

  const agreements = result?.installmentAgreements || []
  const iaSummary = result?.installmentAgreementSummary || {}
  const uncovered = isNum(iaSummary.balanceNotCoveredByActiveIA) ? iaSummary.balanceNotCoveredByActiveIA : 0

  if (!agreements.length) {
    if (!uncovered) return strings.TAXES_NO_IA_ON_FILE_NONE_NEEDED

    const secured = !!result?.lienSummary?.hasActiveLienSecuringNonZeroBalance
    const gap = (iaSummary.periodsNotCoveredByActiveIA || [])[0]
    if (!secured && gap) {
      return `${strings.TAXES_NO_IA_LEAD} ${formatCurrency(uncovered)} ${strings.TAXES_NO_IA_UNCOVERED_ON} ${gap.formName}, ${strings.TAXES_TAX_YEAR} ${gap.taxYear}.`
    }
    return `${strings.TAXES_NO_IA_PREFIX} ${formatCurrency(uncovered)} ${strings.TAXES_NO_IA_SUFFIX}`
  }

  const agreement = agreements[0]
  const isActive = agreement.status === 'Active'
  const opener = isActive
    ? `${strings.TAXES_IA_ACTIVE_SINCE_PREFIX} ${formatLongDate(agreement.startDate)}.`
    : `${agreement.status} ${strings.TAXES_IA_AGREEMENT_STARTED} ${formatLongDate(agreement.startDate)}.`

  let sentence = `${opener} ${formatCurrency(agreement.totalPaid)} ${strings.TAXES_IA_PAID_REMAINING}, ${formatCurrency(agreement.currentBalance)}`
  const period = (agreement.coveredPeriods || [])[0]
  if (period) {
    sentence += ` ${strings.TAXES_IA_REMAINING_COVERING} ${period.formName}, ${strings.TAXES_TAX_YEAR} ${period.taxYear}.`
  } else {
    sentence += ` ${strings.TAXES_IA_REMAINING}.`
  }
  if (uncovered) sentence += ` ${formatCurrency(uncovered)} ${strings.TAXES_IA_OUTSIDE_PLAN}`
  return sentence
}

export const buildLienDetailRows = (lien) => {
  const rows = [
    { label: strings.TAXES_STATUS, value: lien.status || EM_DASH },
    { label: strings.TAXES_FILING_DATE, value: formatDate(lien.filingDate) },
    { label: strings.TAXES_SECURED_BALANCE, value: formatCurrency(lien.currentSecuredBalance) },
    {
      label: (lien.securedPeriods || []).length > 1 ? strings.TAXES_SECURED_PERIODS : strings.TAXES_SECURED_PERIOD,
      value: formatPeriodList(lien.securedPeriods)
    },
    { label: strings.TAXES_REMOVED, value: lien.fullyRemovedDate ? formatDate(lien.fullyRemovedDate) : strings.TAXES_NOT_REMOVED }
  ]
  if (isNum(lien.balanceAtRemoval)) {
    rows.push({ label: strings.TAXES_BALANCE_AT_REMOVAL, value: formatCurrency(lien.balanceAtRemoval) })
  }
  return rows
}

export const buildAgreementDetailRows = (agreement) => {
  const rows = [
    { label: strings.TAXES_STATUS, value: agreement.status || EM_DASH },
    { label: strings.TAXES_START_DATE, value: formatDate(agreement.startDate) }
  ]
  if (agreement.endDate) rows.push({ label: strings.TAXES_END_DATE, value: formatDate(agreement.endDate) })
  rows.push({ label: strings.TAXES_TOTAL_PAID, value: formatCurrency(agreement.totalPaid) })
  rows.push({ label: strings.TAXES_CURRENT_BALANCE, value: formatCurrency(agreement.currentBalance) })
  if (isNum(agreement.balanceAtClose)) {
    rows.push({ label: strings.TAXES_BALANCE_AT_CLOSE, value: formatCurrency(agreement.balanceAtClose) })
  }
  rows.push({
    label: (agreement.coveredPeriods || []).length > 1 ? strings.TAXES_COVERED_PERIODS : strings.TAXES_COVERED_PERIOD,
    value: formatPeriodList(agreement.coveredPeriods)
  })
  return rows
}
